/**
 * Hook to fetch media with authentication and convert to blob URLs
 * This is needed because <img src="..."> doesn't send auth headers
 */

import { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";

// Cache blob URLs to avoid re-fetching
// Key format: "mediaId" or "mediaId-cacheBuster"
const blobUrlCache = new Map<string, string>();

export function useMediaUrl(
  mediaId: number | null,
  cacheBuster?: number
): {
  url: string | null;
  isLoading: boolean;
  error: string | null;
} {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mediaId || !isAuthenticated) {
      setUrl(null);
      return;
    }

    // Capture mediaId after null check for TypeScript
    const id = mediaId;
    const cacheKey = cacheBuster !== undefined ? `${id}-${cacheBuster}` : String(id);

    // Check cache first (but skip if cacheBuster changed)
    const cachedUrl = blobUrlCache.get(cacheKey);
    if (cachedUrl) {
      setUrl(cachedUrl);
      return;
    }

    let cancelled = false;

    async function fetchMedia() {
      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        // Use cache: 'no-store' to bypass browser HTTP cache when cacheBuster changes
        // This ensures we get fresh data after rotation/crop operations
        const response = await fetch(`${API_URL}/media/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          cache: cacheBuster !== undefined ? 'no-store' : 'default',
        });

        if (!response.ok) {
          throw new Error(`Failed to load media: ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        if (!cancelled) {
          // Clear old cached versions of this media ID when cacheBuster changes
          if (cacheBuster !== undefined) {
            blobUrlCache.forEach((_, key) => {
              if (key.startsWith(`${id}-`) && key !== cacheKey) {
                const oldUrl = blobUrlCache.get(key);
                if (oldUrl) URL.revokeObjectURL(oldUrl);
                blobUrlCache.delete(key);
              }
            });
          }
          blobUrlCache.set(cacheKey, blobUrl);
          setUrl(blobUrl);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load media");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchMedia();

    return () => {
      cancelled = true;
    };
  }, [mediaId, cacheBuster, isAuthenticated, getAccessTokenSilently]);

  return { url, isLoading, error };
}

// Cleanup function to revoke blob URLs (call when component unmounts)
export function cleanupMediaUrls() {
  blobUrlCache.forEach((url) => URL.revokeObjectURL(url));
  blobUrlCache.clear();
}

/**
 * Invalidate cached blob URL for a specific media ID.
 * Call this after modifying an image (rotation, crop, etc.)
 */
export function invalidateMediaCache(mediaId: number) {
  // Collect keys to delete first (can't modify Map while iterating)
  const keysToDelete: string[] = [];
  blobUrlCache.forEach((_, key) => {
    if (key === String(mediaId) || key.startsWith(`${mediaId}-`)) {
      keysToDelete.push(key);
    }
  });
  
  // Now delete them
  keysToDelete.forEach(key => {
    const url = blobUrlCache.get(key);
    if (url) URL.revokeObjectURL(url);
    blobUrlCache.delete(key);
  });
}
